// src/features/records/hooks/useActiveRecords.js
import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook to track which records are visible in the viewport
 * Uses IntersectionObserver for efficient visibility detection
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.rootMargin - Margin around viewport for pre-fetching (default: 100px)
 * @param {number} options.threshold - Visibility threshold (default: 0.1 = 10% visible)
 * @returns {Object} { activeRecordIds: Set, observeRecord: Function, unobserveRecord: Function }
 */
export default function useActiveRecords(options = {}) {
  const { 
    rootMargin = '100px', // Pre-fetch 100px before entering viewport
    threshold = 0.1 
  } = options;
  
  const [activeRecordIds, setActiveRecordIds] = useState(new Set());
  const observerRef = useRef(null);
  const elementMapRef = useRef(new Map()); // Map<recordId, Element>

  // Initialize IntersectionObserver
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const updates = new Map();
        
        entries.forEach((entry) => {
          const recordId = entry.target.getAttribute('data-record-id');
          if (recordId) {
            updates.set(recordId, entry.isIntersecting);
          }
        });

        // Update active records in a single state update
        setActiveRecordIds((prevIds) => {
          const newIds = new Set(prevIds);
          updates.forEach((isActive, recordId) => {
            if (isActive) {
              newIds.add(recordId);
            } else {
              newIds.delete(recordId);
            }
          });
          return newIds;
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin,
        threshold
      }
    );

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [rootMargin, threshold]);

  // Observe a record element
  const observeRecord = useCallback((recordId, element) => {
    if (!observerRef.current || !element) return;

    // Store element reference
    elementMapRef.current.set(recordId, element);
    
    // Add data attribute for identification
    element.setAttribute('data-record-id', recordId);
    
    // Start observing
    observerRef.current.observe(element);
  }, []);

  // Stop observing a record element
  const unobserveRecord = useCallback((recordId) => {
    if (!observerRef.current) return;

    const element = elementMapRef.current.get(recordId);
    if (element) {
      observerRef.current.unobserve(element);
      elementMapRef.current.delete(recordId);
      
      // Remove from active set
      setActiveRecordIds((prevIds) => {
        const newIds = new Set(prevIds);
        newIds.delete(recordId);
        return newIds;
      });
    }
  }, []);

  // Check if a specific record is active
  const isRecordActive = useCallback((recordId) => {
    return activeRecordIds.has(recordId);
  }, [activeRecordIds]);

  // Get array of active record IDs
  const activeRecordIdsArray = Array.from(activeRecordIds);

  return {
    activeRecordIds,
    activeRecordIdsArray,
    observeRecord,
    unobserveRecord,
    isRecordActive
  };
}